import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { MessageSquare, Shield, RotateCcw, Save, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  master_admin: 'Master Admin',
  admin: 'Admin',
  moderator: 'Moderator',
  recruiter: 'Recruiter',
  dispatcher: 'Dispatcher',
  safety: 'Safety Officer',
  maintenance: 'Maintenance',
  shop: 'Shop',
  office: 'Office',
  user: 'User',
  driver: 'Driver',
  inspector: 'Inspector',
  mechanic: 'Mechanic'
};

const ROLE_COLORS = {
  master_admin: 'bg-purple-500',
  admin: 'bg-blue-500',
  moderator: 'bg-green-500',
  recruiter: 'bg-pink-500',
  dispatcher: 'bg-orange-500',
  safety: 'bg-red-500',
  user: 'bg-gray-500',
  driver: 'bg-cyan-500',
  inspector: 'bg-yellow-500',
  mechanic: 'bg-amber-500'
};

export default function ChatPermissionsPage() {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const res = await axios.get(`${API}/api/chat/permissions`, { withCredentials: true });
      setPermissions(res.data.permissions || {});
      setAvailableRoles(res.data.available_roles || []);
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load chat permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (senderRole, recipientRole) => {
    const currentAllowed = permissions[senderRole] || [];
    let newAllowed;
    
    if (currentAllowed.includes(recipientRole)) {
      newAllowed = currentAllowed.filter(r => r !== recipientRole);
    } else {
      newAllowed = [...currentAllowed, recipientRole];
    }
    
    // If toggling "all", clear other roles
    if (recipientRole === 'all') {
      newAllowed = currentAllowed.includes('all') ? [] : ['all'];
    } else if (currentAllowed.includes('all')) {
      // If "all" was selected and we're adding a specific role, remove "all"
      newAllowed = newAllowed.filter(r => r !== 'all');
    }
    
    setPermissions({
      ...permissions,
      [senderRole]: newAllowed
    });
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/chat/permissions`, { permissions }, { withCredentials: true });
      toast.success('Chat permissions saved!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all chat permissions to defaults?')) return;
    try {
      const res = await axios.post(`${API}/api/chat/permissions/reset`, {}, { withCredentials: true });
      setPermissions(res.data.permissions || {});
      toast.success('Permissions reset to defaults');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to reset permissions');
    }
  };

  if (user?.role_id !== 'master_admin') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Only Master Admin can configure chat permissions</p>
        </div>
      </DashboardLayout>
    );
  }

  // All possible roles (combine available roles with default ones)
  const allRoles = [...new Set([...availableRoles, ...Object.keys(ROLE_LABELS)])];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <MessageSquare className="w-8 h-8" />
              Chat Permissions
            </h1>
            <p className="text-muted-foreground mt-1">
              Control who can send messages to whom
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset to Defaults
            </Button>
            <Button onClick={handleSaveAll} disabled={!hasChanges || saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Permissions Grid */}
            {allRoles.map(senderRole => {
              const allowed = permissions[senderRole] || [];
              const hasAll = allowed.includes('all');
              
              return (
                <Card key={senderRole}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <Badge className={`${ROLE_COLORS[senderRole] || 'bg-gray-500'} text-white`}>
                        {ROLE_LABELS[senderRole] || senderRole}
                      </Badge>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">can message:</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {/* "All" option */}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`${senderRole}-all`}
                          checked={hasAll}
                          onCheckedChange={() => handleTogglePermission(senderRole, 'all')}
                        />
                        <Label 
                          htmlFor={`${senderRole}-all`}
                          className="text-sm font-medium cursor-pointer"
                        >
                          <Badge variant="outline" className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0">
                            Everyone
                          </Badge>
                        </Label>
                      </div>
                      
                      {/* Individual roles */}
                      {allRoles.filter(r => r !== senderRole).map(recipientRole => (
                        <div key={recipientRole} className="flex items-center space-x-2">
                          <Checkbox
                            id={`${senderRole}-${recipientRole}`}
                            checked={hasAll || allowed.includes(recipientRole)}
                            disabled={hasAll}
                            onCheckedChange={() => handleTogglePermission(senderRole, recipientRole)}
                          />
                          <Label 
                            htmlFor={`${senderRole}-${recipientRole}`}
                            className={`text-sm cursor-pointer ${hasAll ? 'opacity-50' : ''}`}
                          >
                            <Badge variant="outline" className={hasAll || allowed.includes(recipientRole) ? `${ROLE_COLORS[recipientRole] || 'bg-gray-500'} text-white border-0` : ''}>
                              {ROLE_LABELS[recipientRole] || recipientRole}
                            </Badge>
                          </Label>
                        </div>
                      ))}
                    </div>
                    
                    {allowed.length === 0 && (
                      <p className="text-sm text-muted-foreground mt-2 italic">
                        Cannot message anyone (can only receive)
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">How it works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>• <strong>Everyone</strong>: This role can message all users regardless of their role</p>
            <p>• <strong>Checked roles</strong>: This role can send messages to users with those roles</p>
            <p>• <strong>No roles checked</strong>: This role can only receive messages, not send</p>
            <p>• Users can always reply to messages they receive, regardless of permissions</p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
